import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import {
    domainEventBus,
    ROI_CONTOUR_EDIT_EVENT_TYPES,
    RoiContourEditEvent,
} from '~/domain/events';
import type { PlaneMm } from '~/domain/geometry/patientGeometry';
import { InterpolationPreview } from '~/engine/util/algorithms/sdf/ContourOperations';
import {
    getPreviewColor,
    PREVIEW_FILL_ALPHA,
} from '~/hooks/structure-set/previewColor';
import { usePreviewBase } from '~/hooks/structure-set/usePreviewBase';
import { InterpolationScope } from './interpolationPreviewMessage';

export interface InterpolationPreviewResult {
    /** Read-only description of which slices the apply would fill. */
    preview: InterpolationPreview;
    /** Remove the on-image ghost (call before committing or on cancel). */
    clearPreview: () => void;
}

/** Stable reference for the "no preview" snapshot (see useSyncExternalStore). */
const UNAVAILABLE: InterpolationPreview = { status: 'unavailable' };

interface PreviewCacheEntry {
    sdfVersion: number;
    roiNumber: number;
    scope: InterpolationScope;
    plane: PlaneMm | null;
    preview: InterpolationPreview;
}

/** Component-wise equality, so a re-read of an unchanged view is "the same plane". */
function isSamePlane(a: PlaneMm | null, b: PlaneMm | null): boolean {
    if (a === null || b === null) {
        return a === b;
    }
    return (
        a.point[0] === b.point[0] &&
        a.point[1] === b.point[1] &&
        a.point[2] === b.point[2] &&
        a.normal[0] === b.normal[0] &&
        a.normal[1] === b.normal[1] &&
        a.normal[2] === b.normal[2]
    );
}

/**
 * Drives the interpolation tool's pre-apply preview for the selected ROI:
 *
 * - returns a read-only {@link InterpolationPreview} describing which slices
 *   would be filled (for the panel read-out and to gate the Apply button), and
 * - publishes a translucent on-image ghost of the would-be result through the
 *   shared operation-preview slot (the same mechanism Margin/Ring use), so the
 *   user can see the generated contours before committing.
 *
 * Both are computed from the engine's read-only preview (no SDF writes); the
 * ghost runs the exact fill routine the apply uses on a cloned texture, so what
 * is shown is byte-identical to what Apply commits. Recomputes when the
 * selected ROI's contours change (`RoiContoursModifiedEvent`), when the
 * scope changes, or — for the `current` scope only — when the viewed plane
 * changes (the `allEmpty` preview is plane-independent, so its plane dependency
 * is dropped to avoid rescanning on every slice scroll).
 *
 * @param scope - Which interpolation the button would run.
 * @param plane - The viewed plane, or null when unavailable. Only used for
 *     the `current` scope. Compared by value, so callers may pass a fresh
 *     snapshot on every render.
 * @param imageGuided - Whether the apply would run the intensity-guided
 *     refinement. The ghost applies the same refinement so it stays
 *     byte-identical to the commit; the read-out is unaffected (refinement
 *     never changes WHICH slices are filled, only their contour shape).
 */
export function useInterpolationPreview(
    scope: InterpolationScope,
    plane: PlaneMm | null,
    imageGuided: boolean
): InterpolationPreviewResult {
    const { environment, roiNumber: selectedROI } = useActiveEditTarget();
    const { setResult, cleanup } = usePreviewBase({
        environment: environment ?? null,
    });

    // Keep one reference per distinct plane so the cache key and the ghost
    // effect below see a change only when the viewed plane actually moves.
    const stablePlaneRef = useRef<PlaneMm | null>(null);
    const incomingPlane = scope === 'current' ? plane : null;
    if (!isSamePlane(stablePlaneRef.current, incomingPlane)) {
        stablePlaneRef.current = incomingPlane;
    }
    const effectivePlane = stablePlaneRef.current;

    // The SDF mutates in place on a contour edit, so nothing in React changes
    // on its own. Bridge the engine's contour-edit events (RoiContourEditEvent:
    // strokes, plus undo/redo which restore the SDF without a modified event)
    // into React with useSyncExternalStore — as the domain projection hooks do
    // (e.g. useRoiDetailView) — and compute the preview inside the snapshot,
    // caching it by (SDF version, ROI, scope, plane) so the returned reference
    // stays stable until one of those actually changes.
    const structureSetUid = environment?.structureSet?.sopInstanceUID;
    const subscribeToContourEdits = useCallback(
        (onStoreChange: () => void) => {
            const notifyIfSelected = (event: RoiContourEditEvent): void => {
                if (event.roiNumber !== selectedROI) {
                    return;
                }
                // ROI numbers are per structure set: an edit or undo/redo of
                // a same-numbered ROI in another set must not wake this
                // preview.
                if (
                    structureSetUid !== undefined &&
                    event.structureSetUid !== structureSetUid
                ) {
                    return;
                }
                onStoreChange();
            };
            const subscriptions = ROI_CONTOUR_EDIT_EVENT_TYPES.map(
                (eventType) =>
                    domainEventBus.subscribe(eventType, notifyIfSelected)
            );
            return () => {
                subscriptions.forEach((subscription) =>
                    subscription.unsubscribe()
                );
            };
        },
        [selectedROI, structureSetUid]
    );

    const cacheRef = useRef<PreviewCacheEntry | null>(null);
    const preview = useSyncExternalStore<InterpolationPreview>(
        subscribeToContourEdits,
        () => {
            const roi = environment?.structureSet?.getRoi(selectedROI);
            if (!environment || !roi) {
                return UNAVAILABLE;
            }
            // Canonical ROI-level contour version: bumped on every modify
            // finish, texture swap, and undo/redo restore. Monotonic across
            // texture replacement (a fresh SDF's own dataVersion restarts at 0
            // and could ABA-collide), so it is the safe cache key here.
            const sdfVersion = roi.sdfVersion;
            const cached = cacheRef.current;
            if (
                cached &&
                cached.sdfVersion === sdfVersion &&
                cached.roiNumber === selectedROI &&
                cached.scope === scope &&
                cached.plane === effectivePlane
            ) {
                return cached.preview;
            }
            let computed: InterpolationPreview;
            if (scope === 'current') {
                computed =
                    effectivePlane === null
                        ? UNAVAILABLE
                        : environment.previewInterpolateSingleSlice(
                              roi,
                              effectivePlane
                          );
            } else {
                computed = environment.previewInterpolateAllSlices(roi);
            }
            cacheRef.current = {
                sdfVersion,
                roiNumber: selectedROI,
                scope,
                plane: effectivePlane,
                preview: computed,
            };
            return computed;
        }
    );

    // Publish (or clear) the on-image ghost to match the current preview. Only
    // the 'fills' status produces a ghost; gating on the already-computed
    // `preview` avoids the cost of building (cloning + filling) on the no-op
    // states and slices.
    useEffect(() => {
        const roi = environment?.structureSet?.getRoi(selectedROI);
        if (preview.status !== 'fills' || !environment?.image || !roi) {
            cleanup();
            return;
        }
        const texture = environment.buildInterpolatedPreviewTexture(
            roi,
            scope,
            effectivePlane,
            imageGuided
        );
        if (!texture) {
            cleanup();
            return;
        }
        const previewObj = new CustomTargetObject(
            environment.image,
            texture,
            getPreviewColor(roi.color)
        );
        previewObj.renderOptions = { fillAlpha: PREVIEW_FILL_ALPHA };
        setResult(previewObj);
    }, [
        preview,
        environment,
        selectedROI,
        scope,
        effectivePlane,
        imageGuided,
        setResult,
        cleanup,
    ]);

    return { preview, clearPreview: cleanup };
}
